import { isPlatformBrowser, Location } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';
import { CookieService } from 'ngx-cookie-service';
import { ILanguage } from '../core/language.model';
import { LanguageService } from '../core/language.service';
import { ITimeZone } from '../core/time-zone.model';
import { TimeZoneService } from '../core/time-zone.service';
import { POPULAR_LANGUAGES } from '../shared/constants.utils';
import { UiService } from '../shared/ui.service';
import { ConfigService } from '../core/config.service';

/**
 * Main component.
 */
@Component({
    selector: 'app-main',
    templateUrl: './main.component.html',
    styleUrls: ['./main.component.scss'],
    standalone: false
})
export class MainComponent implements OnInit, OnDestroy {
  loginUrl: string;
  filteredLanguages$: Observable<ILanguage[]>;
  popularLanguages$: Observable<ILanguage[]>;
  languages$: Observable<ILanguage[]>;
  timeZones$: Observable<ITimeZone[]>;
  locale: string;
  logoTopSrc: string;
  showServerErrorModal = false;
  preview = false;
  public smallHeight = false;

  private destroy$ = new Subject<void>();
  private unsubscribeLocationFn: VoidFunction; 

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private languageService: LanguageService,
    private timeZoneService: TimeZoneService,
    private cookiesService: CookieService,
    private route: ActivatedRoute,
    public router: Router,
    public uiService: UiService,
    private location: Location,
    private configService: ConfigService
  ) {}

  get signupUrl(): string {
    return `${this.configService.getConfig().ACCOUNT_CLIENT_URL}/apps/create${this.preview ? '?preview=true' : ''}`;
  }

  /**
   * Gets login url.
   *
   * Gets language from cookies.
   *
   * Sets default language.
   *
   * Gets languages and time zones.
   */
  public ngOnInit(): void {
    this.logoTopSrc = `${this.configService.getConfig().PUBLIC_CDN_URL}/hosts/ca-main/logo/images/logo.png`;
    this.loginUrl = this.configService.getConfig().ACCOUNT_CLIENT_URL;
    this.locale = this.cookiesService.get('locale') || 'en';
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => (this.preview = params['preview']));

    // keeping language in sync
    this.languageService.systemLanguage.pipe(takeUntil(this.destroy$)).subscribe(lang => {
      if (lang) {
        this.languageService.setLanguage(lang);
      }
    });

    this.getLanguages();
    this.getTimeZones();
    this.setContentMinHeight();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.unsubscribe();

    if (this.unsubscribeLocationFn) {
      this.unsubscribeLocationFn();
    }
  }

  /**
   * Get languages
   */
  getLanguages(): void {
    this.languages$ = this.languageService.getLanguages();

    this.popularLanguages$ = this.languages$.pipe(
      map(languages => languages.filter(lang => POPULAR_LANGUAGES.includes(lang.id))),
      tap(languages => this.selectedLanguage(languages))
    );

    this.filteredLanguages$ = this.languages$.pipe(
      map(languages => languages.filter(lang => !POPULAR_LANGUAGES.includes(lang.id))),
      tap(languages => this.selectedLanguage(languages))
    );
  }

  /**
   * Get time zones
   */
  getTimeZones(): void {
    this.timeZones$ = this.timeZoneService.getTimezones();
  }

  /**
   * Handle language change
   */
  onLanguageChange(): void {
    this.getLanguages();
    this.getTimeZones();
  }

  /**
   * Controls sign up pop-up.
   */
  managedSignUpPopUp(): void {
    this.uiService.managedSignUpPopUp();
  }

  /**
   * Scrolls to the top of the page.
   */
  scrollPageToTop(): void {
    this.uiService.scrollPageToTop();
  }

  /**
   * Helper method for setting isSelect flag.
   */
  private selectedLanguage(languages: ILanguage[]): void {
    this.locale = this.cookiesService.get('locale') || 'en';
    const selectedLang = languages.find(lang => lang.id === this.locale);

    if (selectedLang) {
      selectedLang.isSelected = true;
    }
  }

  private setContentMinHeight(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.unsubscribeLocationFn = this.location.onUrlChange(path => {      
      if (['/contact', '/pricing'].includes(path)) {
        !this.smallHeight && (this.smallHeight = true);
      } else {
        this.smallHeight && (this.smallHeight = false)
      }
    });
  }
}
